import React, { useEffect, useState } from 'react';
import {
  FaBookmark,
  FaChevronDown,
  FaGift,
  FaImages,
  FaPlay,
  FaRegClock,
  FaShieldAlt,
  FaUndo,
} from 'react-icons/fa';
import {
  DEFAULT_SHARING_SETTINGS,
  DEFAULT_STORY_COACH_ANSWERS,
} from '../services/giftBuilderConfig';
import AfterglowExperience from './AfterglowExperience';
import OpeningExperienceRenderer, { normalizeOpeningMode } from './OpeningExperienceRenderer';
import PrivacyPolicyModal from './PrivacyPolicyModal';
import { getOpeningRevealPreset } from '../services/openingPresets';

const getChapterLabel = (module, index) => {
  const labels = {
    primary_media: 'Photo',
    hero_media: 'Cover',
    media_card: 'Memory',
    text: 'Letter',
    surprise_box: 'Open When',
    flip_card: 'Reveal',
    tiny_museum: 'Tiny Museum',
    voice_postcard: 'Voice',
    memory_map: 'Memory Map',
    then_now: 'Then & Now',
    frame_gallery: 'Frame',
    timeline: 'Timeline',
    polaroid_booth: 'Booth',
    guestbook: 'Blessings',
    keepsake_poster: 'Card',
    song_dedication: 'Song',
  };
  return labels[module?.type] || `Part ${index + 1}`;
};

const getGiftStats = (modules = [], storyAnalysis = {}) => {
  const mediaCount = modules.filter((module) => (
    ['primary_media', 'hero_media', 'media_card', 'voice_postcard', 'song_dedication', 'keepsake_poster', 'then_now', 'frame_gallery'].includes(module.type)
  )).length;
  return {
    chapterCount: modules.length,
    mediaCount,
    minutes: Math.max(1, storyAnalysis.estimatedMinutes || Math.ceil(modules.length * 0.6)),
  };
};

const getHeroMedia = (modules = []) => {
  for (const module of modules) {
    const data = module?.data || {};
    const image = Array.isArray(data.images) ? data.images.find((item) => item?.url) : null;
    const candidates = [
      image?.url,
      data.mediaType !== 'video' ? data.mediaUrl : '',
      data.type !== 'video' ? data.url : '',
      data.coverUrl,
      data.posterUrl,
    ];
    const url = candidates.find((candidate) => typeof candidate === 'string' && candidate.trim());
    if (url) return url;
  }
  return '';
};

const findModuleByType = (modules = [], type) => (
  modules.find((module) => module?.type === type) || null
);

const getWeddingCoupleName = (pageData = {}) => {
  const keepsake = findModuleByType(pageData.modules, 'keepsake_poster');
  return keepsake?.data?.senderName || pageData.ownerName || 'Ava & Julian';
};

const WeddingFavorCover = ({
  pageData,
  onOpenGift,
}) => {
  const coupleName = getWeddingCoupleName(pageData);
  const eventLabel = `${coupleName.toUpperCase()} \u00b7 SEPTEMBER 18`;
  const openingTitle = pageData.openingTitle || 'A little favor from our day';
  const openingSubtitle = pageData.openingSubtitle || 'Thank you for being here. Open the story, sign the blessing wall, and keep this as a tiny piece of tonight.';

  return (
    <header className="lumirae-wedding-favor-cover" data-testid="wedding-favor-cover">
      <div className="lumirae-wedding-favor-screen">
        <div className="lumirae-wedding-favor-pill">{eventLabel}</div>
        <h1>{openingTitle}</h1>
        <p className="lumirae-wedding-favor-body">{openingSubtitle}</p>

        <div className="lumirae-wedding-invitation-card" aria-label="Wedding favor invitation card">
          <span className="gold-rule" />
          <h2>Open slowly</h2>
          <p>A private thank-you, a few memories, and a place to leave your wish.</p>
          <span className="gold-rule" />
        </div>

        <button type="button" className="lumirae-wedding-open-button" onClick={onOpenGift}>
          <span className="lumirae-wedding-button-ribbon" aria-hidden="true" />
          <FaGift className="lumirae-wedding-button-icon" aria-hidden="true" />
          <span className="lumirae-wedding-button-copy">
            <small>Sealed for you</small>
            <strong>Open the Favor</strong>
          </span>
        </button>
        <button type="button" className="lumirae-wedding-secondary-button" onClick={onOpenGift}>
          Sign the Blessing Wall
        </button>
      </div>
    </header>
  );
};

const GiftViewer = ({
  pageData,
  pageId,
  pageExperience,
  storyAnalysis,
  theme,
  activeEditing,
  isLocked,
  openingMode,
  effectiveOpeningMode,
  hasSeenOpening,
  replayOpening,
  showPrivacyPolicy,
  onUpdateModule,
  onRemoveModule,
  onReplayOpening,
  onExitReplay,
  onOpenShareCard,
  onOpenPrivacy,
  onClosePrivacy,
  moduleEndRef,
}) => {
  const [giftStarted, setGiftStarted] = useState(false);
  const [giftFinished, setGiftFinished] = useState(false);
  const stats = getGiftStats(pageData.modules, storyAnalysis);
  const visibleChapters = (pageData.modules || []).slice(0, 6);
  const previewChapters = (pageData.modules || []).slice(0, 3);
  const heroMedia = getHeroMedia(pageData.modules);
  const hasHeroMedia = Boolean(heroMedia);
  const fallbackHeroMedia = getOpeningRevealPreset(pageData.themeId).image;
  const isWeddingFavor = pageData.themeId === 'wedding_favor';
  const readerOpeningMode = openingMode || effectiveOpeningMode;
  const normalizedReaderMode = normalizeOpeningMode(readerOpeningMode);
  const isPagedLayeredGift = ['layered_gift', 'gift_unboxing'].includes(normalizedReaderMode);

  useEffect(() => {
    setGiftStarted(false);
    setGiftFinished(false);
  }, [pageId, pageData?.themeId, pageData?.openingMode]);

  const openGift = (event) => {
    event?.stopPropagation?.();
    setGiftStarted(true);
    setGiftFinished(false);
    window.setTimeout(() => {
      if (isPagedLayeredGift) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        document.querySelector('.lumirae-public-reader')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 80);
  };

  if (giftStarted && isPagedLayeredGift) {
    return (
      <div className={`lumirae-public-view lumirae-recipient-phone-shell lumirae-layered-reading-shell ${isWeddingFavor ? 'is-wedding-favor' : ''}`}>
        <div className="lumirae-public-reader lumirae-public-reader--single-page w-full overflow-visible">
          <OpeningExperienceRenderer
            modules={pageData.modules}
            isEditing={activeEditing}
            updateModule={onUpdateModule}
            removeModule={onRemoveModule}
            pageId={pageId}
            isLocked={isLocked}
            openingMode={readerOpeningMode}
            pageData={pageData}
            onComplete={() => setGiftFinished(true)}
          />
        </div>
        <div ref={moduleEndRef} />

        {giftFinished && (
          <>
            <AfterglowExperience
              modules={pageData.modules}
              ownerName={pageData.ownerName}
              sharingSettings={pageData.sharingSettings || DEFAULT_SHARING_SETTINGS}
              storyCoachAnswers={pageData.storyCoachAnswers || DEFAULT_STORY_COACH_ANSWERS}
              storyAnalysis={storyAnalysis}
              onOpenShareCard={onOpenShareCard}
              onReplayOpening={() => {
                setGiftStarted(false);
                setGiftFinished(false);
                onReplayOpening?.();
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            />

            <button
              onClick={onOpenPrivacy}
              className="lumirae-public-privacy-link"
            >
              <FaShieldAlt /> Privacy Policy
            </button>
          </>
        )}

        <PrivacyPolicyModal
          isOpen={showPrivacyPolicy}
          onAccept={onClosePrivacy}
          onReject={onClosePrivacy}
          infoOnly
        />
      </div>
    );
  }

  return (
    <div className={`lumirae-public-view lumirae-recipient-phone-shell ${isWeddingFavor ? 'is-wedding-favor' : ''}`}>
      {isWeddingFavor ? (
        <WeddingFavorCover pageData={pageData} onOpenGift={openGift} />
      ) : (
        <header
          className={`lumirae-mobile-gift-shell mb-8 relative overflow-hidden ${pageExperience.header}`}
          style={{
            backgroundColor: theme.cardBg,
            borderColor: theme.cardBorder,
            '--shell-accent': theme.accent,
            '--shell-accent-soft': theme.accentSoft || theme.inputBg,
            '--shell-shadow': theme.cardShadow,
          }}
        >
          <div className="lumirae-mobile-gift-topbar">
            <div className="lumirae-mobile-gift-brand">
              <span className="lumirae-mobile-gift-brand-mark"><FaGift /></span>
              <span>Lumirae</span>
            </div>
            <div className="lumirae-mobile-gift-mode">{pageExperience.label}</div>
          </div>

          <div className="lumirae-mobile-gift-hero">
            <div className={`lumirae-mobile-gift-cover ${hasHeroMedia ? 'has-media' : 'has-gift-fallback'}`} aria-hidden="true">
              <div
                className={`lumirae-mobile-gift-cover-photo ${hasHeroMedia ? 'has-media' : 'has-gift-fallback'}`}
                style={{ backgroundImage: `url(${hasHeroMedia ? heroMedia : fallbackHeroMedia})` }}
              >
                {!hasHeroMedia && <FaImages />}
              </div>
              <div className="lumirae-mobile-gift-cover-card">
                <FaBookmark />
              </div>
            </div>

            <div className="lumirae-mobile-gift-copy">
              <div className="lumirae-mobile-gift-kicker">{pageData.openingTitle || 'A Gift Is Waiting'}</div>
              <h1>{pageData.ownerName || 'For You'}</h1>
              <p>{pageData.openingSubtitle || pageExperience.note || 'Open slowly. This was made for one person, one moment, and one feeling.'}</p>
            </div>

            <div className="lumirae-mobile-gift-cta-row">
              <button type="button" className="primary" onClick={openGift}>
                <FaPlay /> Open the Gift
              </button>
            </div>
          </div>

          <div className="lumirae-mobile-gift-stats" aria-label="Gift summary">
            <div>
              <strong>{stats.chapterCount}</strong>
              <span>Gift Pieces</span>
            </div>
            <div>
              <strong>{stats.mediaCount}</strong>
              <span>Media</span>
            </div>
            <div>
              <strong>{stats.minutes}</strong>
              <span>Min Read</span>
            </div>
          </div>

          <div className="lumirae-mobile-gift-chapters" aria-label="Gift chapters">
            {visibleChapters.map((module, index) => (
              <span key={module.id || module._localId || index}>
                Layer {String(index + 1).padStart(2, '0')} / {getChapterLabel(module, index)}
              </span>
            ))}
          </div>

          {previewChapters.length > 0 && (
            <div className="lumirae-mobile-gift-preview-rail" aria-label="Memory capsule preview">
              {previewChapters.map((module, index) => (
                <button
                  key={module.id || module._localId || index}
                  type="button"
                  onClick={openGift}
                >
                  <span>{String(index + 1).padStart(2, '0')}</span>
                  <strong>{getChapterLabel(module, index)}</strong>
                  <small>Open this part</small>
                </button>
              ))}
            </div>
          )}

          <div className="lumirae-mobile-gift-actions">
            {hasSeenOpening && openingMode !== 'full_page' && !replayOpening && (
              <button onClick={onReplayOpening}>
                <FaUndo /> Replay
              </button>
            )}
            {replayOpening && (
              <button onClick={onExitReplay}>
                <FaChevronDown /> Exit Replay
              </button>
            )}
            <div className="lumirae-mobile-gift-readhint">
              <FaRegClock /> Open slowly
            </div>
          </div>
        </header>
      )}

    {giftStarted && (
      <>
        <div className="lumirae-public-reader w-full overflow-visible">
          <OpeningExperienceRenderer
            modules={pageData.modules}
            isEditing={activeEditing}
            updateModule={onUpdateModule}
            removeModule={onRemoveModule}
            pageId={pageId}
            isLocked={isLocked}
            openingMode={readerOpeningMode}
            pageData={pageData}
            onComplete={() => setGiftFinished(true)}
          />
        </div>
        <div ref={moduleEndRef} />

        <AfterglowExperience
          modules={pageData.modules}
          ownerName={pageData.ownerName}
          sharingSettings={pageData.sharingSettings || DEFAULT_SHARING_SETTINGS}
          storyCoachAnswers={pageData.storyCoachAnswers || DEFAULT_STORY_COACH_ANSWERS}
          storyAnalysis={storyAnalysis}
          onOpenShareCard={onOpenShareCard}
          onReplayOpening={() => {
            setGiftStarted(false);
            onReplayOpening?.();
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
        />

        <button
          onClick={onOpenPrivacy}
          className="lumirae-public-privacy-link"
        >
          <FaShieldAlt /> Privacy Policy
        </button>
      </>
    )}

    <PrivacyPolicyModal
      isOpen={showPrivacyPolicy}
      onAccept={onClosePrivacy}
      onReject={onClosePrivacy}
      infoOnly
    />

  </div>
  );
};

export default GiftViewer;
